import { type FC, useMemo } from "react";

import { Link } from "../../common/components/Link";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { type AccountInfoData } from "../../AccountSelection/AccountSelectionTabelView/api/GetAccountList";
import { Col, type ColSizes, Row } from "../../common/components/Layout";
import { EmailOrderQuoteAccount, EmailOrderQuoteDropdownType } from "./EmailOrderQuoteAccount";
import { type EmailOrderQuoteData } from "../types";
import { userCanSetMyCompanyOrders, userCanSetMyCompanyQuotes } from "../../AccountSettings/helper/permissions";
import { makeAccountDataIntoMultiSelect } from "../helper/makeAccountData";
import { dropdownAccountSelectionToAccountData } from "../helper/dropdownAccountSelectionToData";
import { selectedOrderQuoteAccountDropdown } from "../helper/dropdownDefaultSelectionList";
import { useTranslation } from "react-i18next";

interface EmailsOrderListTableRowProps {
  entry: EmailOrderQuoteData;
  headerSizes: number[];
  headerStrings: string[];
  availableAccounts: AccountInfoData[];
  onRowEdit?: (data: EmailOrderQuoteData) => void;
  onRowUpdate?: (data: EmailOrderQuoteData) => void;
  onRowDelete?: (data: EmailOrderQuoteData) => void;
}

export const EmailsOrderListTableRow: FC<EmailsOrderListTableRowProps> = ({
  entry,
  headerSizes,
  headerStrings,
  availableAccounts,
  onRowEdit,
  onRowUpdate,
  onRowDelete,
}) => {
  const { t } = useTranslation();

  const orderAccountsDropdown = useMemo(
    () => makeAccountDataIntoMultiSelect({ list: availableAccounts ?? [], email: entry.orderAcknowledgement ?? [] }),
    [availableAccounts, entry.orderAcknowledgement]
  );
  const quoteAccountsDropdown = useMemo(
    () => makeAccountDataIntoMultiSelect({ list: availableAccounts ?? [], email: entry.quoteAcknowledgement ?? [] }),
    [availableAccounts, entry.quoteAcknowledgement]
  );

  const onAccountsChange = (type: EmailOrderQuoteDropdownType, ids: string) => {
    // clone the initial object
    const payload: EmailOrderQuoteData = {
      ...entry,
    };
    // grab the new accounts based on selection
    const selection = dropdownAccountSelectionToAccountData(ids, availableAccounts);
    // determin if is order or quote
    if (type === EmailOrderQuoteDropdownType.ORDER) {
      payload.orderAcknowledgement = selection;
    } else {
      payload.quoteAcknowledgement = selection;
    }
    // request to update
    onRowUpdate && onRowUpdate(payload);
  };

  return (
    <Row>
      <Col
        size={headerSizes[0] as ColSizes}
        className={"scha__order-quote-settings__main__table__cols__" + headerStrings[0] + "__body col-body"}
      >
        {entry.recipientEmail}
      </Col>

      {userCanSetMyCompanyOrders && (
        <Col
          size={headerSizes[1] as ColSizes}
          className={"scha__order-quote-settings__main__table__cols__" + headerStrings[1] + "__body col-body"}
        >
          <EmailOrderQuoteAccount
            type={EmailOrderQuoteDropdownType.ORDER}
            list={orderAccountsDropdown}
            selected={selectedOrderQuoteAccountDropdown(orderAccountsDropdown)}
            onChange={(ids) => {
              onAccountsChange(EmailOrderQuoteDropdownType.ORDER, ids);
            }}
          />
        </Col>
      )}

      {userCanSetMyCompanyQuotes && (
        <Col
          size={headerSizes[2] as ColSizes}
          className={"scha__order-quote-settings__main__table__cols__" + headerStrings[2] + "__body col-body"}
        >
          <EmailOrderQuoteAccount
            type={EmailOrderQuoteDropdownType.QUOTE}
            list={quoteAccountsDropdown}
            selected={selectedOrderQuoteAccountDropdown(quoteAccountsDropdown)}
            onChange={(ids) => {
              onAccountsChange(EmailOrderQuoteDropdownType.QUOTE, ids);
            }}
          />
        </Col>
      )}

      <Col size={1} className={"scha__order-quote-settings__main__table__cols__edit__body col-body"}>
        <Link onClick={() => onRowEdit && onRowEdit(entry)} iconBefore={<Icon type={IconType.Pencil} />}>
          {t("page.orderQuoteSettings.table.button.edit")}
        </Link>
      </Col>
      <Col size={1} className={"scha__order-quote-settings__main__table__cols__delete__body col-body"}>
        <Link onClick={() => onRowDelete && onRowDelete(entry)} iconBefore={<Icon type={IconType.Bin} />}>
          {t("page.orderQuoteSettings.table.button.delete")}
        </Link>
      </Col>
    </Row>
  );
};
